import { useEffect, useState, useCallback } from 'react';
import { useIsFetching, useIsMutating } from '@tanstack/react-query';

/**
 * Non-blocking banner that shows a connection status indicator.
 *
 * - "Connection interrupted" when offline (navigator.onLine === false)
 * - "Refreshing data..." when back online but queries are re-fetching
 * - Auto-dismisses when all queries settle
 *
 * Renders as a slim, non-blocking bar at the top of the page so the user
 * can see the status without losing their place in the content.
 */
export function ReconnectBanner() {
  const [isOffline, setIsOffline] = useState(
    typeof navigator !== 'undefined' ? !navigator.onLine : false,
  );
  const [wasOffline, setWasOffline] = useState(false);
  const fetchingCount = useIsFetching();
  const isRefetching = fetchingCount > 0;
  const [visible, setVisible] = useState(false);
  const [message, setMessage] = useState('');

  const handleOffline = useCallback(() => {
    setWasOffline(true);
    setIsOffline(true);
    setMessage('Connection interrupted — showing saved data');
    setVisible(true);
  }, []);

  const handleOnline = useCallback(() => {
    setIsOffline(false);
    setMessage('Back online — refreshing...');
    setVisible(true);
  }, []);

  useEffect(() => {
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, [handleOffline, handleOnline]);

  // When back online and refetching, show the refreshing message.
  // When back online and done refetching, show a brief "updated" then dismiss.
  useEffect(() => {
    if (!wasOffline) return;

    if (!isOffline && !isRefetching) {
      setMessage('Data updated');
      const timer = setTimeout(() => {
        setVisible(false);
        setWasOffline(false);
      }, 2000);
      return () => clearTimeout(timer);
    }

    if (!isOffline && isRefetching) {
      setMessage('Back online — refreshing...');
    }
  }, [isOffline, isRefetching, wasOffline]);

  // Also detect offline via periodic check (navigator.onLine is not always reliable)
  useEffect(() => {
    const interval = setInterval(() => {
      const online = navigator.onLine;
      if (!online && !isOffline) {
        handleOffline();
      } else if (online && isOffline) {
        handleOnline();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [isOffline, handleOffline, handleOnline]);

  if (!visible) return null;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[9999] pointer-events-none"
      style={{ pointerEvents: 'none' }}
    >
      <div
        className={`
          mx-auto mt-1 max-w-fit rounded-full px-4 py-1.5 text-xs font-medium shadow-lg
          transition-all duration-300 ease-in-out
          ${isOffline
            ? 'bg-amber-900/80 text-amber-200 border border-amber-700/50'
            : 'bg-emerald-900/80 text-emerald-200 border border-emerald-700/50'
          }
        `}
        style={{ pointerEvents: 'auto' }}
      >
        <span className="inline-flex items-center gap-2">
          {isOffline ? (
            <>
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
              {message}
            </>
          ) : (
            <>
              <span className={`inline-block w-1.5 h-1.5 rounded-full ${isRefetching ? 'bg-emerald-400 animate-pulse' : 'bg-emerald-400'}`} />
              {message}
            </>
          )}
        </span>
      </div>
    </div>
  );
}